/* eslint-disable @typescript-eslint/no-explicit-any */
import { SyncableDataType } from '../../app/data/types'

// 이 기기가 업로드로 서버에 (재)등장시킨 항목의 soft-delete 방지 유예 시간 (5분)
// 업로드(생성·복원) 성공 직전에 시작된 서버 GET 응답에는 그 항목이 아직 없을 수 있는 타이밍 갭 보호
const MERGE_SOFT_DELETE_GRACE_MS = 5 * 60 * 1000

// localStringId → 생성·복원 업로드 성공 시각(ms, 기기 시계).
// updated_at 기준으로 판정하면 이 기기가 최근 PUT한 서버 기존 항목까지 유예를 받아,
// 다른 기기가 삭제해도 최대 5분간 살아 있었다. 진행 중이던 GET은 앱 재시작을 넘지 않으므로 메모리로 충분하다.
const recentlyUploadedAt = new Map<string, number>()

// grace period 내에 이 기기가 생성·복원 업로드한 항목인지 판별 (기록이 없으면 유예 없음)
const isWithinGracePeriod = (item: { localStringId?: unknown }): boolean => {
  if (item.localStringId === undefined || item.localStringId === null) return false
  const key = String(item.localStringId)
  const uploadedAt = recentlyUploadedAt.get(key)
  if (uploadedAt === undefined) return false
  if (Date.now() - uploadedAt < MERGE_SOFT_DELETE_GRACE_MS) return true
  recentlyUploadedAt.delete(key)
  return false
}

// 서버 목록 미존재 1회만으로 즉시 soft-delete하지 않는 테이블 (사진류)
// 일시적 목록 누락 응답 한 번으로 업로드 완료된 사진이 기기에서 사라지는 것을 방지
const TWO_STRIKE_STORE_KEYS = new Set(['defect_photos', 'facility_photos', 'quality_result_photos'])

// 서버 목록 미존재 마킹 키 (fields_updated_at 내 저장, need_restore와 같은 패턴)
const SERVER_MISSING_KEY = 'server_missing_at'

// 로컬 스탬프가 서버보다 앞서면서 값이 다르면 서버로 재전파하는 필드 (테이블별)
// 대표사진(sort_key)·보고서 선정(is_main) 변경이 업로드 완료 처리와 경합해 dirty가 해제된 채
// 서버에 닿지 못한 상태를 병합 시점에 교정한다
const LOCAL_AHEAD_RESYNC_FIELDS: Record<string, string[]> = {
  defect_photos: ['sort_key', 'is_main', 'drawing'],
  facility_photos: ['drawing'],
}

// 서버 스탬프가 존재할 때 재전파를 인정하는 최소 선행 폭.
// 서버 스탬프는 서버 시계, 로컬 스탬프는 기기 시계라 근소한 차이는 시계 skew일 수 있어
// 이 폭 이내면 서버 값(다른 사용자의 변경 가능성)을 존중한다. 서버 스탬프 부재(생성 이후 미변경)는 폭 무관.
const LOCAL_AHEAD_RESYNC_SKEW_MARGIN_MS = 5 * 60 * 1000

// 키 정렬 직렬화 (서버 jsonb는 키 순서를 바꿔 돌려주므로 순서 무관 비교용)
const stableStringify = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
  if (v && typeof v === 'object') {
    const obj = v as Record<string, unknown>
    return `{${Object.keys(obj)
      .sort()
      .filter(key => obj[key] !== undefined)
      .map(key => `${JSON.stringify(key)}:${stableStringify(obj[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(v) ?? ''
}

// 동기화 값 비교 (SQLite의 0/1과 서버 boolean, null/undefined/'' 차이를 동일 취급)
// JSON 필드(drawing 등)는 키 순서 무관 비교, 파싱 안 된 JSON 문자열도 객체로 풀어 비교
const isSameSyncValue = (a: unknown, b: unknown): boolean => {
  const normalize = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return ''
    if (typeof v === 'boolean') return v ? '1' : '0'
    if (v === 'true') return '1'
    if (v === 'false') return '0'
    if (typeof v === 'object') return stableStringify(v)
    if (typeof v === 'string' && (v.startsWith('{') || v.startsWith('['))) {
      try {
        return stableStringify(JSON.parse(v))
      } catch {
        return v
      }
    }
    return String(v)
  }
  return normalize(a) === normalize(b)
}

// 마킹 후 이 시간이 지난 뒤의 재확인만 삭제 확정으로 인정 (연속 병합의 동일 장애 재사용 방지)
const SERVER_MISSING_CONFIRM_MS = 60 * 1000

// fields_updated_at을 객체로 정규화 (문자열/누락/파싱 실패 모두 방어)
const parseFieldUpdates = (raw: unknown): { [key: string]: string } => {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw as { [key: string]: string }
}

// shape_data를 객체로 정규화 (문자열/누락/파싱 실패 모두 방어)
// 손상 문자열 row 하나가 병합 사이클 전체를 중단시키지 않도록 한다
const parseShapeData = (raw: unknown): { shapeId?: unknown } | null => {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return raw as { shapeId?: unknown }
}

// 파일 부재 확정 등으로 서버에 올라가지 못한 로컬 파일 경로 사진 여부
const isLocalFileUrl = (url: unknown): boolean =>
  typeof url === 'string' &&
  (url.startsWith('local-file://') ||
    url.startsWith('android-local-file:/') ||
    url.startsWith('android-file:/'))

/**
 * 서버 목록에 없는 동기화 완료(mfl=0) 로컬 row 처리.
 * 사진류는 2회 연속(60초 이상 간격) 미확인 시에만 soft-delete하고,
 * 파일 부재 확정 row('기기에서 삭제됨' 배지 유지 대상)는 삭제하지 않는다.
 * @param sqliteItem 서버 목록에서 발견되지 않은 로컬 row
 * @param storeKey 테이블 이름
 * @returns 유지/마킹/soft-delete 처리된 row
 */
const resolveMissingFromServer = (sqliteItem: any, storeKey: string): any => {
  if (TWO_STRIKE_STORE_KEYS.has(storeKey)) {
    if (isLocalFileUrl(sqliteItem.url)) {
      return sqliteItem
    }
    const fieldUpdates = parseFieldUpdates(sqliteItem.fields_updated_at)
    const markedAtRaw = fieldUpdates[SERVER_MISSING_KEY]
    const markedAt = markedAtRaw ? new Date(markedAtRaw).getTime() : NaN
    if (isNaN(markedAt)) {
      console.warn(
        `[PhotoGuard] merge: ${storeKey} id=${sqliteItem.id} 서버 목록 미존재 1회차 - 유지하고 마킹`,
      )
      return {
        ...sqliteItem,
        fields_updated_at: { ...fieldUpdates, [SERVER_MISSING_KEY]: Merge.getCurrentTime() },
      }
    }
    if (Date.now() - markedAt < SERVER_MISSING_CONFIRM_MS) {
      return sqliteItem
    }
    console.warn(
      `[PhotoGuard] merge: ${storeKey} id=${sqliteItem.id} 서버 목록 미존재 재확인 - soft-delete`,
    )
  }
  return {
    ...sqliteItem,
    deleted_at: Merge.getCurrentTime(),
    modifiedFromLocal: false,
  }
}

const Merge = {
  /**
   * 생성·복원 업로드 성공을 기록한다 (병합 soft-delete 유예의 기준 시각).
   * @param localStringId 업로드한 항목의 localStringId
   */
  markRecentlyUploaded: (localStringId: string): void => {
    const now = Date.now()
    // 만료 항목 정리 (유예 판정에 다시 걸리지 않은 항목이 쌓이지 않도록)
    for (const [key, at] of recentlyUploadedAt) {
      if (now - at >= MERGE_SOFT_DELETE_GRACE_MS) recentlyUploadedAt.delete(key)
    }
    recentlyUploadedAt.set(localStringId, now)
  },

  /**
   * 로컬(SQLite) 데이터와 서버 데이터를 병합하여 최종 결과를 반환합니다.
   * fields_updated_at을 기준으로 각 필드별로 최신 데이터를 선택합니다.
   *
   * @param {any[]} sqliteData - 로컬 SQLite에 저장된 데이터 배열 (localStringId, modifiedFromLocal 포함)
   * @param {any[]} serverData - 서버에서 받은 데이터 배열
   * @returns {Promise<any[]>} 병합된 최종 데이터 배열
   *
   * @description
   * 주요 실행 케이스:
   * 1. 빈 데이터 처리
   *    - serverData가 없으면 sqliteData 그대로 반환
   *    - sqliteData가 없으면 serverData 그대로 반환
   *
   * 2. ID 기반 매칭
   *    - id를 기준으로 로컬과 서버 데이터를 매칭하여 병합
   *    - 로컬에만 있는 데이터: 그대로 결과에 추가
   *    - 서버에만 있는 데이터: modifiedFromLocal=false로 추가
   *    - 양쪽 모두 있는 데이터: 필드별로 병합
   *
   * 3. fields_updated_at 기반 필드별 병합
   *    - fields_updated_at은 각 필드의 최종 수정 시간을 JSON 형태로 저장
   *    - 예: { memo: "2025-11-23T10:30:00.000+09:00", status: "2025-11-23T11:00:00.000+09:00" }
   *    - 각 필드마다 업데이트 시간을 비교하여 더 최신 데이터 선택
   *
   * 4. modifiedFromLocal 플래그 관리
   *    - 로컬에만 있는 변경사항이 있으면 true
   *    - 로컬 데이터가 서버보다 최신인 필드가 있으면 true
   *    - 나머지 경우는 false
   *
   * 5. 특수 필드 처리
   *    - updated_at, created_at: 항상 서버 데이터 우선
   *    - localStringId: 로컬 데이터에만 존재, 병합 시 유지
   *
   * @example
   * const sqliteData = [
   *   {
   *     id: 1,
   *     name: 'John',
   *     memo: 'Local memo',
   *     fields_updated_at: { name: '2025-11-23T09:00:00.000Z', memo: '2025-11-23T10:00:00.000Z' },
   *     localStringId: 'local_123',
   *     modifiedFromLocal: true
   *   }
   * ]
   *
   * const serverData = [
   *   {
   *     id: 1,
   *     name: 'Jane',
   *     memo: 'Server memo',
   *     fields_updated_at: { name: '2025-11-23T11:00:00.000Z', memo: '2025-11-23T08:00:00.000Z' }
   *   }
   * ]
   *
   * const result = await Merge.mergeBothData(sqliteData, serverData)
   * // 결과: name은 서버(더 최신), memo는 로컬(더 최신) 사용
   * // result[0].name === 'Jane'
   * // result[0].memo === 'Local memo'
   * // result[0].modifiedFromLocal (로컬이 더 최신인 필드가 있음)
   */
  mergeBothData: (sqliteData: any[], serverData: any[], storeKey: string): any[] => {
    // 서버 데이터가 없으면 로컬 데이터 중 modifiedFromLocal인 것만 반환
    if (!sqliteData || sqliteData.length === 0) {
      return serverData
    }
    if (!serverData || serverData.length === 0) {
      const result: any[] = []
      for (const sqliteItem of sqliteData) {
        const isDeleted = sqliteItem.deleted_at !== null && sqliteItem.deleted_at !== undefined
        const isModifiedFromLocal = sqliteItem.modifiedFromLocal

        if (!isDeleted && isModifiedFromLocal) {
          // 로컬에서 수정된 데이터는 서버에 동기화 필요
          result.push(sqliteItem)
        } else if (!isDeleted && !isModifiedFromLocal) {
          // 아직 서버에 생성되지 않은 로컬 전용 항목은 soft-delete 방지
          const isLocalOnly = String(sqliteItem.id).includes('_need_to_upload')
          if (isLocalOnly || isWithinGracePeriod(sqliteItem)) {
            result.push(sqliteItem)
          } else {
            // 서버에서 받았던 데이터인데 서버에 없음 → 서버에서 삭제됨 (사진류는 2회 확인 후)
            result.push(resolveMissingFromServer(sqliteItem, storeKey))
          }
        } else {
          // 이미 삭제된 데이터는 그대로 유지
          result.push(sqliteItem)
        }
      }
      return result
    }

    // 서버 데이터를 id와 localStringId 기준으로 Map 생성 (이중 인덱싱)
    const serverDataMapById = new Map<string, any>()
    const serverDataMapByLocalStringId = new Map<string, any>()

    for (const serverItem of serverData) {
      // 1. id로 인덱싱
      if (serverItem.id !== undefined && serverItem.id !== null) {
        const normalizedId = String(serverItem.id)
        serverDataMapById.set(normalizedId, serverItem)
      }
      // 2. localStringId로도 인덱싱 (race condition 대응)
      if (serverItem.localStringId) {
        const normalizedLocalStringId = String(serverItem.localStringId)
        serverDataMapByLocalStringId.set(normalizedLocalStringId, serverItem)
      }
    }
    const mergedResult: any[] = []
    // 1단계: sqliteData를 순회하며 서버 데이터와 병합
    for (const sqliteItem of sqliteData) {
      const normalizedLocalId = String(sqliteItem.id)
      const localStringId = sqliteItem.localStringId

      // 1차: ID로 매칭 시도
      let matchingServerItem = serverDataMapById.get(normalizedLocalId)

      // 2차: ID 매칭 실패 시 localStringId로 매칭 시도 (race condition 대응)
      if (!matchingServerItem && localStringId) {
        const normalizedLocalStringId = String(localStringId)
        matchingServerItem = serverDataMapByLocalStringId.get(normalizedLocalStringId)
      }

      // 3차 매칭 여부 플래그 (race condition으로 content 기반 매칭된 경우 / defect 전용)
      let matchedByContent = false

      // 3차: _need_to_upload인 경우 content 기반 매칭 (race condition 대응)
      // defect 전용
      // Background sync 중에 initData가 호출되어 서버 데이터가 먼저 저장된 경우 대응
      if (
        !matchingServerItem &&
        normalizedLocalId.includes('_need_to_upload') &&
        storeKey === 'defects'
      ) {
        const localShapeData = parseShapeData(sqliteItem.shape_data)
        const localShapeId = localShapeData?.shapeId
        // 무도형(NO_SHAPE) 결함은 shapeId가 없어 floorplan+created_at으로 매칭
        // (서버 무도형 dedup의 409 후 재바인딩이 무도형에서도 동작해야 영구 미동기화가 안 생김)
        const isLocalShapeless = !localShapeData
        const localFloorplanId = sqliteItem.floorplan_id
        const localCreatedAt = sqliteItem.created_at ? new Date(sqliteItem.created_at).getTime() : 0

        if ((localShapeId || (isLocalShapeless && localCreatedAt > 0)) && localFloorplanId) {
          for (const [, serverItem] of serverDataMapById) {
            const serverShapeData = parseShapeData(serverItem.shape_data)
            const serverShapeId = serverShapeData?.shapeId
            const serverFloorplanId = serverItem.floorplan_id
            const serverCreatedAt = serverItem.created_at
              ? new Date(serverItem.created_at).getTime()
              : 0

            // 도형: shapeId 일치 / 무도형: 양쪽 모두 shape_data 부재
            const isShapeMatch = localShapeId
              ? serverShapeId === localShapeId
              : serverShapeData == null

            // 3가지 조건 모두 일치해야 매칭
            if (
              isShapeMatch &&
              String(serverFloorplanId) === String(localFloorplanId) &&
              Math.abs(serverCreatedAt - localCreatedAt) < 1000 // 1초 이내 오차 허용
            ) {
              console.info(
                `[Merge] Race condition 감지: 로컬(${localStringId})과 서버(${serverItem.id})가 같은 항목으로 매칭됨 (shapeId: ${localShapeId})`,
              )
              matchingServerItem = serverItem
              matchedByContent = true
              break
            }
          }
        }
      }

      if (!matchingServerItem) {
        // 서버에 없는 로컬 데이터: 조건에 따라 처리
        const isDeleted = sqliteItem.deleted_at !== null && sqliteItem.deleted_at !== undefined
        const isModifiedFromLocal = sqliteItem.modifiedFromLocal

        if (!isDeleted && isModifiedFromLocal) {
          // 로컬에서 수정된 데이터는 서버에 동기화 필요
          mergedResult.push(sqliteItem)
        } else if (!isDeleted && !isModifiedFromLocal) {
          // 아직 서버에 생성되지 않은 로컬 전용 항목은 soft-delete 방지
          const isLocalOnly = String(sqliteItem.id).includes('_need_to_upload')
          if (isLocalOnly || isWithinGracePeriod(sqliteItem)) {
            mergedResult.push(sqliteItem)
          } else {
            // 서버에서 받은 데이터인데 서버에 없음 → 서버에서 삭제됨 (사진류는 2회 확인 후)
            mergedResult.push(resolveMissingFromServer(sqliteItem, storeKey))
          }
        }
        continue
      }

      // 개별 항목 병합 시작
      const mergedItem = { ...sqliteItem }

      // fields_updated_at을 파싱하여 각 필드별 업데이트 시간 확인 (파싱 실패 방어)
      const sqliteFieldUpdates = parseFieldUpdates(sqliteItem.fields_updated_at)
      const serverFieldUpdates = parseFieldUpdates(matchingServerItem.fields_updated_at)

      // 서버 데이터의 모든 필드 수집
      const serverFields = new Set<string>()
      for (const property in matchingServerItem) {
        if (
          property !== 'fields_updated_at' &&
          property !== 'updated_at' &&
          property !== 'created_at' &&
          property !== 'localStringId' &&
          property !== 'modifiedFromLocal' &&
          property !== 'task_id'
        ) {
          serverFields.add(property)
        }
      }

      // 로컬 데이터의 모든 필드 수집
      const sqliteFields = new Set<string>()
      for (const property in sqliteItem) {
        if (
          property !== 'fields_updated_at' &&
          property !== 'updated_at' &&
          property !== 'created_at' &&
          property !== 'localStringId' &&
          property !== 'modifiedFromLocal' &&
          property !== 'task_id'
        ) {
          sqliteFields.add(property)
        }
      }

      // 2단계: 각 필드별로 fields_updated_at 비교하여 최신 데이터 선택
      const fieldComparisonResults: string[] = []
      // 서버보다 앞선(아직 서버로 전송되지 못한) 로컬 필드 존재 여부
      let hasLocalAheadField = false

      for (const property of serverFields) {
        const sqliteUpdateTime = sqliteFieldUpdates[property]
        const serverUpdateTime = serverFieldUpdates[property]

        // 양쪽 모두 업데이트 시간이 있는 경우
        if (sqliteUpdateTime && serverUpdateTime) {
          // 날짜 문자열을 Date 객체로 변환하여 비교
          const sqliteDate = new Date(sqliteUpdateTime).getTime()
          const serverDate = new Date(serverUpdateTime).getTime()

          if (serverDate > sqliteDate) {
            // 서버가 더 최신: 서버 데이터 사용
            mergedItem[property] = matchingServerItem[property]
            fieldComparisonResults.push(`    ${property}: 서버 최신 (서버 우선)`)
          } else if (sqliteDate > serverDate) {
            // 로컬이 더 최신: 로컬 데이터 유지
            hasLocalAheadField = true
            fieldComparisonResults.push(`    ${property}: 로컬 최신 (로컬 유지)`)
          } else {
            fieldComparisonResults.push(`    ${property}: 동일 (로컬 유지)`)
          }
          // 같은 경우는 이미 sqliteItem을 복사했으므로 로컬 데이터 유지
        } else if (!sqliteUpdateTime && serverUpdateTime) {
          // 로컬에 없고 서버에만 있는 필드: 서버 데이터 추가
          mergedItem[property] = matchingServerItem[property]
          fieldComparisonResults.push(`    ${property}: 서버만 존재 (서버 추가)`)
        } else if (sqliteUpdateTime && !serverUpdateTime) {
          // 서버에 없고 로컬에만 있는 필드: 로컬 데이터 유지
          hasLocalAheadField = true
          fieldComparisonResults.push(`    ${property}: 로컬만 존재 (로컬 유지)`)
        }
      }

      // 3단계: 로컬에만 있는 필드들 확인
      const localOnlyFields: string[] = []
      for (const property of sqliteFields) {
        if (!serverFields.has(property)) {
          const sqliteUpdateTime = sqliteFieldUpdates[property]
          if (sqliteUpdateTime) {
            localOnlyFields.push(property)
          }
        }
      }
      if (localOnlyFields.length > 0) {
        hasLocalAheadField = true
      }

      // 4단계: fields_updated_at 병합
      const mergedFieldUpdates = { ...sqliteFieldUpdates }
      for (const [field, time] of Object.entries(serverFieldUpdates)) {
        const sqliteTime = sqliteFieldUpdates[field]
        if (!sqliteTime || new Date(time as string).getTime() > new Date(sqliteTime).getTime()) {
          mergedFieldUpdates[field] = time
        }
      }
      // 서버 목록에서 재확인된 항목은 미존재 마킹 해제 (연속 미확인만 삭제로 이어지도록)
      if (mergedFieldUpdates[SERVER_MISSING_KEY]) {
        delete mergedFieldUpdates[SERVER_MISSING_KEY]
      }
      mergedItem.fields_updated_at = mergedFieldUpdates

      // 5단계: modifiedFromLocal 플래그 설정
      if (matchedByContent) {
        // 3차 매칭(race condition)으로 매칭된 경우:
        // 서버 id를 사용하여 중복 CREATE 방지.
        // 단, 생성 이후 로컬에서 추가 수정(번호풍선 이동 등)이 있어 서버보다 앞선 필드가
        // 있으면 dirty를 유지해야 UPDATE로 재전송된다 — 무조건 false로 내리면
        // 로컬은 최신처럼 보이지만 서버에는 영영 반영되지 않는 유실이 생긴다.
        mergedItem.id = matchingServerItem.id
        mergedItem.modifiedFromLocal = Boolean(sqliteItem.modifiedFromLocal) && hasLocalAheadField
        console.info(
          `[Merge] Race condition 해결: localStringId(${sqliteItem.localStringId}) 유지, 서버 id(${matchingServerItem.id}) 사용, modifiedFromLocal=${mergedItem.modifiedFromLocal}`,
        )
      } else {
        // 일반 매칭: sqliteItem의 기존 modifiedFromLocal 값을 유지
        mergedItem.modifiedFromLocal = sqliteItem.modifiedFromLocal
      }

      // 6단계: updated_at, created_at은 서버 데이터 우선
      if (matchingServerItem.updated_at) {
        mergedItem.updated_at = matchingServerItem.updated_at
      }
      if (matchingServerItem.created_at) {
        mergedItem.created_at = matchingServerItem.created_at
      }

      // 6-1단계: merge 기반 soft-delete 복구
      // 로컬에서 deleted_at이 설정되었지만 fields_updated_at에 deleted_at이 없고,
      // 서버에서는 삭제되지 않은 경우 → merge가 잘못 soft-delete한 것이므로 복구
      const localDeletedAt = mergedItem.deleted_at
      const serverDeletedAt = matchingServerItem.deleted_at
      const hasDeletedAtField = mergedFieldUpdates['deleted_at']
      if (localDeletedAt && !serverDeletedAt && !hasDeletedAtField) {
        mergedItem.deleted_at = null
        mergedItem.modifiedFromLocal = false
      }
      // 6-2단계: 삭제 전파 누락 교정 (사진류)
      // 로컬은 사용자 의도로 삭제(fields_updated_at.deleted_at 보유)됐는데 서버에는
      // 살아있고 전파 예정도 아닌(mfl=false) 경우 - 삭제 시각과 서버 최종 갱신 시각을
      // 비교해 늦은 쪽을 채택한다.
      else if (
        localDeletedAt &&
        !serverDeletedAt &&
        hasDeletedAtField &&
        !mergedItem.modifiedFromLocal &&
        TWO_STRIKE_STORE_KEYS.has(storeKey)
      ) {
        // 기기 시계는 신뢰 가능하다는 전제로 최신-우선(LWW) 비교
        const deletedStampTime = new Date(String(hasDeletedAtField)).getTime()
        // updated_at이 ''/null/무효 문자열이면 NaN → 비교가 항상 false가 되어
        // 사용자가 지운 사진이 부활한다. 판정 불가일 땐 로컬 삭제를 신뢰(안전한 쪽).
        const rawServerUpdatedAt = matchingServerItem.updated_at
        const serverUpdatedTime = rawServerUpdatedAt
          ? new Date(rawServerUpdatedAt).getTime()
          : Number.NaN
        if (
          isNaN(deletedStampTime) ||
          isNaN(serverUpdatedTime) ||
          deletedStampTime > serverUpdatedTime
        ) {
          // 로컬 삭제가 최신이거나 판정 불가 → 전파가 누락된 상태로 보고 삭제를 서버로 재전파
          mergedItem.modifiedFromLocal = true
          console.warn(
            `[PhotoGuard] merge: ${storeKey} id=${mergedItem.id} 삭제 전파 누락 감지 - 서버로 재전파`,
          )
        } else {
          // 서버가 삭제 이후에 갱신됨(복원 등) → 서버 상태 우선으로 로컬 삭제 해제
          mergedItem.deleted_at = null
          delete mergedFieldUpdates['deleted_at']
          mergedItem.modifiedFromLocal = false
          console.warn(
            `[PhotoGuard] merge: ${storeKey} id=${mergedItem.id} 서버 갱신이 더 최신 - 로컬 삭제 해제`,
          )
        }
      }

      // 6-3단계: 대표사진(sort_key)·보고서 선정(is_main) 전파 누락 교정 (사진류)
      // 로컬 스탬프가 서버보다 앞서는데(로컬 유지) 값이 서버와 다르면 서버에 반영되지 못한 변경이므로
      // dirty를 되살려 update job으로 재전파한다 (서버 미생성·삭제 row 제외)
      const resyncFields = LOCAL_AHEAD_RESYNC_FIELDS[storeKey]
      if (
        resyncFields &&
        !mergedItem.modifiedFromLocal &&
        !mergedItem.deleted_at &&
        !String(mergedItem.id).includes('_need_to_upload')
      ) {
        const staleField = resyncFields.find(field => {
          const localTime = sqliteFieldUpdates[field]
            ? new Date(sqliteFieldUpdates[field]).getTime()
            : Number.NaN
          if (isNaN(localTime)) return false
          const serverTime = serverFieldUpdates[field]
            ? new Date(serverFieldUpdates[field]).getTime()
            : Number.NaN
          if (!isNaN(serverTime) && localTime - serverTime < LOCAL_AHEAD_RESYNC_SKEW_MARGIN_MS) {
            return false
          }
          return !isSameSyncValue(mergedItem[field], matchingServerItem[field])
        })
        if (staleField) {
          mergedItem.modifiedFromLocal = true
          console.warn(
            `[PhotoGuard] merge: ${storeKey} id=${mergedItem.id} ${staleField} 전파 누락 감지 - 서버로 재전파`,
          )
        }
      }

      mergedResult.push(mergedItem)
      // 처리한 서버 데이터는 Map에서 제거 (양쪽 Map 모두에서 제거)
      const serverItemId = String(matchingServerItem.id)
      const serverItemLocalStringId = matchingServerItem.localStringId
        ? String(matchingServerItem.localStringId)
        : null

      serverDataMapById.delete(serverItemId)
      if (serverItemLocalStringId) {
        serverDataMapByLocalStringId.delete(serverItemLocalStringId)
      }
    }

    // 7단계: 로컬에 없고 서버에만 있는 데이터 추가
    for (const [_, serverItem] of serverDataMapById) {
      mergedResult.push({
        ...serverItem,
        modifiedFromLocal: false,
      })
    }

    // 8단계: localStringId가 없고 id가 유효한 숫자인 경우 localStringId 설정
    for (const item of mergedResult) {
      if (!item.localStringId) {
        const idStr = String(item.id)
        const isValidNumericId = !idStr.includes('_need_to_upload') && !isNaN(Number(item.id))
        if (isValidNumericId) {
          item.localStringId = idStr
        }
      }
    }
    return mergedResult
  },

  /**
   * fields_updated_at 객체에 특정 필드의 업데이트 시간을 현재 시간으로 설정합니다.
   *
   * @param {Object} fieldUpdates - 기존 fields_updated_at 객체 (각 필드의 업데이트 시간을 저장)
   * @param {string} key - 업데이트할 필드 이름
   * @param {any} [_value] - 사용되지 않는 매개변수 (호환성 유지용)
   * @returns {Object} 업데이트된 fields_updated_at 객체
   *
   * @description
   * 주요 실행 케이스:
   * 1. 기존 필드 업데이트
   *    - fieldUpdates에 이미 존재하는 필드의 시간을 현재 시간으로 갱신
   *
   * 2. 새 필드 추가
   *    - fieldUpdates에 없던 필드를 새로 추가하고 현재 시간 설정
   *
   * 3. ISO 8601 형식
   *    - 모든 시간은 ISO 8601 형식으로 저장 (YYYY-MM-DDTHH:mm:ss.sssZ)
   *
   * @example
   * const fieldUpdates = {
   *   name: '2025-11-23T09:00:00.000Z',
   *   memo: '2025-11-23T10:00:00.000Z'
   * }
   *
   * // 기존 필드 업데이트
   * const updated = Merge.setFieldUpdate(fieldUpdates, 'name')
   * // updated = {
   * //   name: '2025-11-23T12:00:00.000Z',  // 현재 시간으로 갱신
   * //   memo: '2025-11-23T10:00:00.000Z'
   * // }
   *
   * // 새 필드 추가
   * const updated2 = Merge.setFieldUpdate(fieldUpdates, 'status')
   * // updated2 = {
   * //   name: '2025-11-23T09:00:00.000Z',
   * //   memo: '2025-11-23T10:00:00.000Z',
   * //   status: '2025-11-23T12:00:00.000Z'  // 새로 추가
   * // }
   */
  setFieldUpdate: (
    fieldUpdates: { [key: string]: any },
    key: string,
    _value?: any,
  ): { [key: string]: any } => {
    const fieldUpdate = { ...fieldUpdates }
    // 기존 스탬프보다 항상 뒤인 시각으로 — 기기 시계가 서버보다 뒤져도 LWW에서 지지 않도록
    fieldUpdate[key] = Merge.getMonotonicTime(fieldUpdates)
    return fieldUpdate
  },
  /**
   * 현재 시간을 한국 시간대(+09:00) 기준 ISO 8601 형식으로 반환합니다.
   *
   * @returns {string} 한국 시간대 포함 ISO 8601 형식 문자열 (예: 2025-11-23T11:00:00.000+09:00)
   *
   * @description
   * UTC 시간에 9시간을 더하여 한국 시간대(KST, Korea Standard Time)로 변환합니다.
   * fields_updated_at, updated_at, created_at 등 모든 시간 필드에 일관된 형식을 적용합니다.
   *
   * @example
   * const currentTime = Merge.getCurrentTime()
   * // 반환 예시: "2025-11-23T11:00:00.000+09:00"
   */
  getCurrentTime: (): string => {
    return Merge.formatKSTTime(Date.now())
  },

  // 주어진 UTC 밀리초를 한국 시간대(+09:00) ISO 8601 문자열로 변환
  formatKSTTime: (utcMs: number): string => {
    const kstOffset = 9 * 60 // 한국 시간대 오프셋 (9시간 = 540분)
    const kstTime = new Date(utcMs + kstOffset * 60 * 1000)

    const year = kstTime.getUTCFullYear()
    const month = String(kstTime.getUTCMonth() + 1).padStart(2, '0')
    const day = String(kstTime.getUTCDate()).padStart(2, '0')
    const hours = String(kstTime.getUTCHours()).padStart(2, '0')
    const minutes = String(kstTime.getUTCMinutes()).padStart(2, '0')
    const seconds = String(kstTime.getUTCSeconds()).padStart(2, '0')
    const milliseconds = String(kstTime.getUTCMilliseconds()).padStart(3, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+09:00`
  },

  /**
   * 기존 fields_updated_at의 어떤 스탬프보다도 뒤인 현재 스탬프를 반환합니다.
   * 기기 시계가 서버보다 뒤처진 경우, 서버 응답으로 받아둔 스탬프보다 과거 시각이
   * 찍혀 방금 한 수정이 필드 LWW 병합에서 서버(수정 전 값)에 지는 것을 방지합니다.
   * @param fieldUpdates 해당 row의 기존 fields_updated_at 객체
   * @returns 단조 증가가 보장된 KST ISO 8601 문자열
   */
  getMonotonicTime: (fieldUpdates: unknown): string => {
    const nowMs = Date.now()
    // 24시간 이상 미래의 스탬프는 손상 데이터로 보고 "개별" 제외 —
    // 전체 max에 대한 일괄 컷오프는 손상 스탬프 하나가 정상 스탬프들의 보호까지 가리므로 금지
    const FUTURE_CORRUPT_THRESHOLD_MS = 24 * 60 * 60 * 1000
    let maxExistingMs = 0
    for (const value of Object.values(parseFieldUpdates(fieldUpdates))) {
      const t = new Date(String(value)).getTime()
      if (isNaN(t)) continue
      if (t - nowMs >= FUTURE_CORRUPT_THRESHOLD_MS) {
        console.warn(`[Merge] 손상 의심 미래 스탬프 무시 (24h+ future): ${value}`)
        continue
      }
      if (t > maxExistingMs) maxExistingMs = t
    }
    if (maxExistingMs >= nowMs) {
      return Merge.formatKSTTime(maxExistingMs + 1)
    }
    return Merge.formatKSTTime(nowMs)
  },

  // 2025-12-09T05:25:14.030Z 형식의 현재시간 문자열 반환
  getCurrentUTCTime: (): string => {
    return new Date().toISOString()
  },
  /**
   * 현재 시간을 unixtime으로 반환합니다.
   * @description 기존 호환성용 함수
   * @returns {number} 현재 시간을 unixtime으로 반환
   */
  getUnixtime: (): number => {
    return parseInt(`${Date.now()}`.slice(0, 10))
  },

  sortupdatedAt: (data: any[]): any[] => {
    return data.sort((a: { updated_at: string }, b: { updated_at: string }) => {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    })
  },
  sortcreatedAt: (data: any[], reverse: boolean = false): any[] => {
    return data.sort((a: { created_at: string }, b: { created_at: string }) => {
      return reverse
        ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        : new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })
  },

  findChangedField: (before: any, after: any, noMerge: boolean = false): { [key: string]: any } => {
    const changedFields: { [key: string]: any } = {}

    // proxy 등의 래핑된 객체를 일반 객체로 변환
    const normalizedBefore = before ? JSON.parse(JSON.stringify(before)) : {}
    const normalizedAfter = after ? JSON.parse(JSON.stringify(after)) : {}

    // before의 fields_updated_at 가져오기
    const existingFieldsUpdatedAt = normalizedBefore.fields_updated_at || {}
    // 기존 스탬프(서버 응답으로 받은 것 포함)보다 항상 뒤인 스탬프 사용
    const stampTime = Merge.getMonotonicTime(existingFieldsUpdatedAt)

    // after의 키만 순회 (after에 없으면 무시)
    for (const field of Object.keys(normalizedAfter)) {
      // fields_updated_at 자체는 비교 대상에서 제외
      if (field === 'fields_updated_at') continue

      // before에도 해당 키가 있는 경우에만 비교
      if (field in normalizedBefore) {
        const beforeValue = normalizedBefore[field]
        const afterValue = normalizedAfter[field]

        // 값이 다른 경우에만 changedFields에 추가
        // 깊은 비교를 위해 JSON.stringify 사용
        if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
          changedFields[field] = stampTime
        }
      }
    }

    // 기존 fields_updated_at에 변경된 필드 병합
    const mergedFieldsUpdatedAt = {
      ...existingFieldsUpdatedAt,
      ...changedFields,
    }
    if (noMerge) {
      return changedFields
    }

    return mergedFieldsUpdatedAt
  },

  /**
   * 객체를 순회하면서 null, undefined, 빈 문자열('')을 제외한 값들만 포함된 객체를 반환합니다.
   *
   * @param {SyncableDataType} item - 필터링할 객체
   * @returns {SyncableDataType} - 유효한 값들만 포함된 객체
   *
   * @description
   * 다음 값들을 제외합니다:
   * - undefined
   * - null
   * - 빈 문자열 ('')
   *
   * @example
   * const data = {
   *   id: 1,
   *   name: 'John',
   *   age: null,
   *   memo: '',
   *   email: undefined,
   *   address: 'Seoul'
   * }
   * const result = Merge.returnHasValue(data)
   * // 결과: { id: 1, name: 'John', address: 'Seoul' }
   */
  returnHasValue: (item: Partial<SyncableDataType>): Partial<SyncableDataType> => {
    const result: Record<string, unknown> = {}
    const keys = Object.keys(item)

    for (const key of keys) {
      const value = item[key as keyof Partial<SyncableDataType>]

      // null, undefined, 빈 문자열을 제외한 값들만 포함
      if (value !== undefined && value !== null && value !== '') {
        result[key] = value
      }
    }

    return result as unknown as Partial<SyncableDataType>
  },
}

export default Merge
